import { Box, Container, Paper, type SxProps } from '@mui/material';
import type { ElementType, ReactNode } from 'react';

const COMPONENT_MAP = {
    Paper,
    div: 'div',
    section: 'section',
} as const;

type ComponentName = keyof typeof COMPONENT_MAP;

interface PageBackgroundProps {
    children: ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
    sx?: SxProps;
    rootSx?: SxProps;
    /**
     * Render the content container as this element/component.
     * Accepts an MUI/React component (e.g. `Paper`) or a known string name
     * (`"Paper"`, `"div"`, `"section"`). Defaults to MUI `Container`.
     */
    component?: ElementType | ComponentName;
}

const resolveComponent = (component?: ElementType | ComponentName): ElementType | undefined => {
    if (component == null) return undefined;
    if (typeof component === 'string' && component in COMPONENT_MAP) {
        return COMPONENT_MAP[component as ComponentName] as ElementType;
    }
    return component as ElementType;
};

const PageBackground = ({
    children,
    maxWidth = 'xl',
    sx = {},
    rootSx = {},
    component,
}: PageBackgroundProps) => {
    const contentComponent = resolveComponent(component);

    return (
        <Box
            {...(contentComponent ? { component: contentComponent } : {})}
            sx={{
                position: 'relative',
                minHeight: '100dvh',
                width: '100%',
                overflow: 'hidden',
                ...rootSx,
            }}
        >
            {/* Content Layer */}
            <Container maxWidth={maxWidth} sx={{ p: 2, ...sx }}>
                {children}
            </Container>
        </Box>
    );
};

export default PageBackground;
